import { formatNumberBasedOnUser } from "utils/namesManipulation";

import type { EnergyAnalysisRow, EnergyAnalysisSplitDimension } from "../model/types";

import { buildCategoryTotals, collectDimensionKeys, collectYears, sumValues } from "./aggregate";
import { buildEnergyAnalysisAxisMax } from "./chartStyle";
import { buildCategoryColorMap } from "./colors";
import { toEnergyAnalysisLabel } from "./labels";

const formatChartNumber = (value: number) => formatNumberBasedOnUser(value, 2);
const commonGrid = { bottom: 50, containLabel: true, left: 16, right: 72, top: 24 };

const referenceRows = (rows: EnergyAnalysisRow[], snapshotYear?: number) => {
  const years = collectYears(rows);
  const year = snapshotYear !== undefined && years.includes(snapshotYear) ? snapshotYear : years[years.length - 1];
  return { rows: rows.filter((row) => row.year === year), year };
};

const stableReferenceTotals = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension,
  snapshotYear?: number
) => {
  const reference = referenceRows(rows, snapshotYear);
  const stableTotals = buildCategoryTotals(rows, dimension);
  const selectedTotals = buildCategoryTotals(reference.rows, dimension);
  const selectedByKey = new Map(selectedTotals.map((item) => [item.key, item]));
  const selectedTotal = selectedTotals.reduce((total, item) => total + item.value, 0);
  const totals = stableTotals.map((stableItem) => {
    const value = selectedByKey.get(stableItem.key)?.value ?? 0;
    return {
      ...stableItem,
      share: selectedTotal === 0 ? 0 : value / selectedTotal,
      value,
    };
  });
  const snapshotValueMax = Math.max(
    ...collectYears(rows).flatMap((year) =>
      buildCategoryTotals(
        rows.filter((row) => row.year === year),
        dimension
      ).map((item) => item.value)
    ),
    0
  );
  return { reference, snapshotValueMax, totals };
};

export const buildDimensionRankedBarOption = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension,
  unit: string,
  snapshotYear?: number
) => {
  const { snapshotValueMax, totals: stableTotals } = stableReferenceTotals(
    rows,
    dimension,
    snapshotYear
  );
  const totals = [...stableTotals].reverse();
  const range = buildEnergyAnalysisAxisMax(snapshotValueMax);
  return {
    dataZoom: [],
    series: [
      {
        data: totals.map((item) => ({
          itemStyle: { color: item.color },
          share: item.share,
          value: item.value,
        })),
        label: {
          formatter: ({ data }: { data: { share: number; value: number } }) =>
            `${formatChartNumber(data.value)}  ·  ${formatChartNumber(data.share * 100)}%`,
          position: "right",
          show: true,
        },
        type: "bar",
      },
    ],
    tooltip: {
      valueFormatter: (value: number) => `${formatChartNumber(value)} ${unit}`,
      trigger: "axis",
    },
    xAxis: [
      {
        axisLabel: { formatter: formatChartNumber },
        interval: range.interval,
        max: range.max,
        type: "value",
      },
    ],
    yAxis: [{ boundaryGap: true, data: totals.map((item) => item.label), type: "category" }],
  };
};

export const buildDimensionDoughnutOption = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension,
  unit: string,
  snapshotYear?: number
) => {
  const { totals } = stableReferenceTotals(rows, dimension, snapshotYear);
  const total = totals.reduce((result, item) => result + item.value, 0);
  return {
    dataZoom: [],
    graphic: [
      {
        left: "center",
        style: {
          rich: {
            total: { fontSize: 22, fontWeight: 700, lineHeight: 28 },
            unit: { color: "#687086", fontSize: 11, lineHeight: 18 },
          },
          text: `{total|${formatChartNumber(total)}}\n{unit|${unit}}`,
          textAlign: "center",
        },
        top: "39%",
        type: "text",
      },
    ],
    legend: { bottom: 0, type: "scroll" },
    series: [
      {
        center: ["50%", "45%"],
        data: totals.map((item) => ({
          itemStyle: { color: item.color },
          name: item.label,
          value: item.value,
        })),
        label: {
          formatter: ({ percent }: { percent: number }) =>
            `${formatChartNumber(percent)}%`,
          position: "inside",
        },
        radius: ["42%", "68%"],
        type: "pie",
      },
    ],
    tooltip: {
      valueFormatter: (value: number) => `${formatChartNumber(value)} ${unit}`,
      trigger: "item",
    },
  };
};

export const buildDimensionTreemapOption = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension,
  unit: string,
  snapshotYear?: number
) => {
  const { totals } = stableReferenceTotals(rows, dimension, snapshotYear);
  const total = totals.reduce((result, item) => result + item.value, 0);
  return {
    animation: false,
    dataZoom: [],
    series: [
      {
        animation: false,
        animationDuration: 0,
        animationDurationUpdate: 0,
        bottom: 16,
        breadcrumb: { show: false },
        data: totals.map((item) => ({
          itemStyle: { color: item.color },
          name: item.label,
          value: item.value,
        })),
        itemStyle: {
          borderColor: "#ffffff",
          borderRadius: 8,
          borderWidth: 4,
          gapWidth: 4,
        },
        label: {
          formatter: ({ name, value }: { name: string; value: number }) =>
            `${name}\n${formatChartNumber(total > 0 ? (value / total) * 100 : 0)}%`,
          overflow: "truncate",
          show: true,
        },
        left: 16,
        roam: false,
        right: 16,
        sort: false,
        top: 16,
        type: "treemap",
        universalTransition: false,
      },
    ],
    tooltip: {
      valueFormatter: (value: number) => `${formatChartNumber(value)} ${unit}`,
      trigger: "item",
    },
  };
};

export const buildDimensionStackedAreaOption = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension,
  unit: string
) => {
  const years = collectYears(rows);
  const keys = collectDimensionKeys(rows, dimension);
  const colors = buildCategoryColorMap(keys);
  return {
    dataZoom: [],
    grid: commonGrid,
    legend: { bottom: 0, type: "scroll" },
    series: keys.map((key) => ({
      areaStyle: { opacity: 0.78 },
      data: years.map((year) =>
        sumValues(rows.filter((row) => row.year === year && row[dimension] === key))
      ),
      emphasis: { focus: "series" },
      itemStyle: { color: colors[key] },
      lineStyle: { width: 1 },
      name: toEnergyAnalysisLabel(key),
      stack: "total",
      symbol: "none",
      type: "line",
    })),
    tooltip: {
      trigger: "axis",
      valueFormatter: (value: number) => `${formatChartNumber(value)} ${unit}`,
    },
    xAxis: [{ boundaryGap: false, data: years, type: "category" }],
    yAxis: [{ axisLabel: { formatter: formatChartNumber }, name: unit, type: "value" }],
  };
};
